import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Tooltip,
  Divider,
  Alert,
  Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SuperAdminService, { FiscalDevice, FiscalDeviceCreateDto } from '../../services/SuperAdminService';

interface FiscalDeviceModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  companyName: string;
}

const emptyForm: FiscalDeviceCreateDto = {
  companyId: '',
  vkn: '',
  authorizedPerson: '',
  phone: '',
  accountName: '',
  city: '',
  branch: '',
  brand: '',
  model: '',
  serialNumber: '',
  usageArea: '',
  gmuFeeCollected: false,
  activationDate: null,
  expiryDate: null,
  invoiceStatus: '',
  notes: '',
};

function getExpiryStatus(expiryDate: string | null): 'ok' | 'warning' | 'expired' | 'none' {
  if (!expiryDate) return 'none';
  const now = new Date();
  const expiry = new Date(expiryDate);
  if (expiry < now) return 'expired';
  const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 30) return 'warning';
  return 'ok';
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('tr-TR');
  } catch {
    return '-';
  }
}

function daysRemaining(expiryDate: string | null): number | null {
  if (!expiryDate) return null;
  const now = new Date();
  const expiry = new Date(expiryDate);
  return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

const FiscalDeviceModal: React.FC<FiscalDeviceModalProps> = ({ open, onClose, companyId, companyName }) => {
  const [devices, setDevices] = useState<FiscalDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FiscalDeviceCreateDto>({ ...emptyForm, companyId });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDevices = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      setError(null);
      const service = SuperAdminService.getInstance();
      const result = await service.getFiscalDevices(companyId);
      setDevices(result);
    } catch (err: any) {
      setError(err.message || 'Cihazlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (open && companyId) {
      loadDevices();
      setShowForm(false);
      setEditingId(null);
      setForm({ ...emptyForm, companyId });
    }
  }, [open, companyId, loadDevices]);

  const handleChange = (field: keyof FiscalDeviceCreateDto, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, companyId });
    setShowForm(true);
  };

  const handleEdit = (device: FiscalDevice) => {
    setEditingId(device.id);
    setForm({
      companyId: device.companyId,
      vkn: device.vkn || '',
      authorizedPerson: device.authorizedPerson || '',
      phone: device.phone || '',
      accountName: device.accountName || '',
      city: device.city || '',
      branch: device.branch || '',
      brand: device.brand || '',
      model: device.model || '',
      serialNumber: device.serialNumber || '',
      usageArea: device.usageArea || '',
      gmuFeeCollected: device.gmuFeeCollected,
      activationDate: device.activationDate ? device.activationDate.split('T')[0] : null,
      expiryDate: device.expiryDate ? device.expiryDate.split('T')[0] : null,
      invoiceStatus: device.invoiceStatus || '',
      notes: device.notes || '',
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm, companyId });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const service = SuperAdminService.getInstance();

      const payload = {
        ...form,
        activationDate: form.activationDate || null,
        expiryDate: form.expiryDate || null,
      };

      if (editingId) {
        await service.updateFiscalDevice(editingId, payload);
      } else {
        await service.addFiscalDevice(payload);
      }

      await loadDevices();
      handleCancel();
    } catch (err: any) {
      setError(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (device: FiscalDevice) => {
    if (!window.confirm(`"${device.brand} ${device.model} - ${device.serialNumber || 'Seri No Yok'}" cihazını silmek istediğinize emin misiniz?`)) return;
    try {
      setError(null);
      const service = SuperAdminService.getInstance();
      await service.deleteFiscalDevice(device.id, companyId);
      await loadDevices();
    } catch (err: any) {
      setError(err.message || 'Silme sırasında hata oluştu');
    }
  };

  const expiredCount = devices.filter(d => getExpiryStatus(d.expiryDate) === 'expired').length;
  const warningCount = devices.filter(d => getExpiryStatus(d.expiryDate) === 'warning').length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" PaperProps={{ sx: { minHeight: '80vh' } }}>
      <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Box>
          <Typography variant="h6" component="span">Yazarkasa Cihazları</Typography>
          <Typography variant="body2" component="div" sx={{ opacity: 0.9 }}>{companyName}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={`${devices.length} Cihaz`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          {expiredCount > 0 && (
            <Chip icon={<WarningAmberIcon sx={{ color: '#fff !important' }} />} label={`${expiredCount} Süresi Dolmuş`} size="small" sx={{ bgcolor: '#f44336', color: 'white' }} />
          )}
          {warningCount > 0 && (
            <Chip icon={<WarningAmberIcon sx={{ color: '#fff !important' }} />} label={`${warningCount} Süresi Yaklaşıyor`} size="small" sx={{ bgcolor: '#ff9800', color: 'white' }} />
          )}
          <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} disabled={showForm}>
            Yeni Cihaz Ekle
          </Button>
        </Box>

        <Collapse in={showForm}>
          <Box sx={{ mx: 2, mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {editingId ? 'Cihaz Düzenle' : 'Yeni Cihaz Ekle'}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <TextField size="small" label="VKN" value={form.vkn || ''} onChange={e => handleChange('vkn', e.target.value)} />
              <TextField size="small" label="Yetkili" value={form.authorizedPerson || ''} onChange={e => handleChange('authorizedPerson', e.target.value)} />
              <TextField size="small" label="Telefon" value={form.phone || ''} onChange={e => handleChange('phone', e.target.value)} />
              <TextField size="small" label="Cari Unvan" value={form.accountName || ''} onChange={e => handleChange('accountName', e.target.value)} />
              <TextField size="small" label="İl" value={form.city || ''} onChange={e => handleChange('city', e.target.value)} />
              <TextField size="small" label="Şube" value={form.branch || ''} onChange={e => handleChange('branch', e.target.value)} />
              <TextField size="small" label="Marka" value={form.brand || ''} onChange={e => handleChange('brand', e.target.value)} />
              <TextField size="small" label="Model" value={form.model || ''} onChange={e => handleChange('model', e.target.value)} />
              <TextField size="small" label="Seri No" value={form.serialNumber || ''} onChange={e => handleChange('serialNumber', e.target.value)} />
              <TextField size="small" label="Kullanım Yeri" value={form.usageArea || ''} onChange={e => handleChange('usageArea', e.target.value)} />
              <TextField size="small" label="Aktivasyon Tarihi" type="date" value={form.activationDate || ''} onChange={e => handleChange('activationDate', e.target.value || null)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="Bitiş Tarihi" type="date" value={form.expiryDate || ''} onChange={e => handleChange('expiryDate', e.target.value || null)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="Fatura Durumu" value={form.invoiceStatus || ''} onChange={e => handleChange('invoiceStatus', e.target.value)} />
              <FormControlLabel
                control={<Switch checked={form.gmuFeeCollected} onChange={e => handleChange('gmuFeeCollected', e.target.checked)} />}
                label="GMÜ Ücreti Alındı"
                sx={{ ml: 0 }}
              />
              <TextField size="small" label="Notlar" value={form.notes || ''} onChange={e => handleChange('notes', e.target.value)} multiline sx={{ gridColumn: { md: 'span 2' } }} />
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={saving}>İptal</Button>
              <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}>
                {saving ? 'Kaydediliyor...' : editingId ? 'Güncelle' : 'Kaydet'}
              </Button>
            </Box>
          </Box>
        </Collapse>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 6, color: 'text.secondary' }}>
            <Typography variant="h6">Henüz yazarkasa cihazı eklenmemiş</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>Yeni cihaz eklemek için yukarıdaki butonu kullanın.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mx: 2, mb: 2, maxWidth: 'calc(100% - 32px)' }} elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>SN</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>VKN</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Yetkili</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefon</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Cari</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>İl</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Şube</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Marka</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Seri No</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Kullanım Yeri</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>GMÜ Ücret</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Aktivasyon</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Bitiş</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Fatura Durumu</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device, index) => {
                  const status = getExpiryStatus(device.expiryDate);
                  const days = daysRemaining(device.expiryDate);
                  const rowBg = status === 'expired' ? 'rgba(244,67,54,0.08)' : status === 'warning' ? 'rgba(255,152,0,0.08)' : 'inherit';
                  return (
                    <TableRow key={device.id} sx={{ bgcolor: rowBg, '&:hover': { bgcolor: status === 'expired' ? 'rgba(244,67,54,0.14)' : status === 'warning' ? 'rgba(255,152,0,0.14)' : '#f5f5f5' } }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{device.vkn || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.authorizedPerson || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.phone || '-'}</TableCell>
                      <TableCell>
                        <Tooltip title={device.accountName || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{device.accountName || '-'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{device.city || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{device.branch || '-'}</TableCell>
                      <TableCell>{device.brand || '-'}</TableCell>
                      <TableCell>{device.model || '-'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{device.serialNumber || '-'}</TableCell>
                      <TableCell>{device.usageArea || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={device.gmuFeeCollected ? 'Evet' : 'Hayır'}
                          size="small"
                          color={device.gmuFeeCollected ? 'success' : 'default'}
                          variant={device.gmuFeeCollected ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(device.activationDate)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {formatDate(device.expiryDate)}
                          {status === 'expired' && <Chip label="Süresi Dolmuş" size="small" color="error" sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }} />}
                          {status === 'warning' && days !== null && <Chip label={`${days} gün`} size="small" color="warning" sx={{ ml: 0.5, height: 20, fontSize: '0.65rem' }} />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={device.invoiceStatus || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>{device.invoiceStatus || '-'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Düzenle">
                          <IconButton size="small" onClick={() => handleEdit(device)} color="primary"><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton size="small" onClick={() => handleDelete(device)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FiscalDeviceModal;
